'use client';

import { useEffect } from 'react';
import { Link } from '@/i18n/routing';
import OrganizerCard from '@/components/OrganizerCard';
import { OrganizerLikeButton } from '@/components/OrganizerLikeButton';
import { useSubscriptions } from '@/hooks/useSubscriptions';

interface Organization {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  logo_url?: string | null;
  website?: string | null;
  is_liked?: boolean;
  unique_visits?: number | null;
}

interface OrganizationsListProps {
  organizations: Organization[];
  emptyText: string;
}

export default function OrganizationsList({ organizations, emptyText }: OrganizationsListProps) {
  const { mergeSubscriptionsFromServer } = useSubscriptions();

  useEffect(() => {
    if (!organizations.length) return;
    const items = organizations.map((org) => ({ entity_id: org.id, is_liked: org.is_liked }));
    mergeSubscriptionsFromServer('organization', items);
  }, [organizations, mergeSubscriptionsFromServer]);

  if (organizations.length === 0) {
    return (
      <div className="p-8 text-center bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl">
        <p className="text-slate-600 dark:text-slate-400">{emptyText}</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {organizations.map((org) => (
        <Link
          key={org.id}
          href={{ pathname: '/[slug]', params: { slug: org.slug } }}
          className="no-underline block w-full group"
        >
          <div className="w-full flex flex-col gap-4 p-6 pr-28 pt-5 pb-5 bg-white dark:bg-slate-900/50 dark:backdrop-blur-sm rounded-2xl border border-slate-200 dark:border-slate-800 hover:border-blue-500 dark:hover:border-blue-400 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300 h-full relative">
            <div className="absolute top-4 right-4 z-10 flex items-center gap-1.5" onClick={(e) => e.preventDefault()}>
              <OrganizerLikeButton organizationId={org.id} uniqueVisits={org.unique_visits} />
            </div>
            <div className="min-w-0 flex-1">
              <OrganizerCard
                organization={org}
                variant="default"
                wrapper="none"
              />
              {org.website && (
                <div className="mt-3">
                  <span className="text-xs text-blue-500 hover:underline inline-flex items-center gap-1">
                    Visit Website
                  </span>
                </div>
              )}
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}
